import { Linkable } from '@/components/blocks/Linkable';
import { buttonSX } from '@/components/content/ForgotPassword/styles/container';
import { LoginSx } from '@/components/content/Login/styles/container';
import { useForgotPassword, validateForgotPasswordEmail } from '@/data/Content/ForgotPassword';
import { useLocalization } from '@/data/Localization';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';

type Props = {
	onSuccess: (email: string) => void;
	onGoToLogin?: () => void;
};

export const SendForgotPasswordEmail = ({ onSuccess, onGoToLogin }: Props) => {
	const nls = useLocalization('ForgotPassword');
	const RouteLocal = useLocalization('Routes');
	const { sendForgotPasswordCode } = useForgotPassword();

	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	const [emailVerify, setEmailVerify] = useState('');
	const [emailVerifyError, setEmailVerifyError] = useState('');
	const [loading, setLoading] = useState(false);

	const isValidEmail = validateForgotPasswordEmail(email);
	const emailsMatch = email === emailVerify && emailVerify.length > 0;
	const isFormReady = isValidEmail && emailsMatch;

	const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setEmail(val);
		if (val.length > 0 && !!emailVerify && emailVerify !== val) {
			setEmailVerifyError(nls.Msgs.NoMatchEmails.t());
		} else {
			setEmailVerifyError('');
		}
	};

	const handleEmailBlur = () => {
		if (email.trim() === '') return;
		if (!isValidEmail) {
			setEmailError(nls.Msgs.InvalidFormat.t());
		}
	};

	const handleEmailVerifyChange = (e: ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setEmailVerify(val);
		if (val.length > 0 && email !== val) {
			setEmailVerifyError(nls.Msgs.NoMatchEmails.t());
		} else {
			setEmailVerifyError('');
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isFormReady) return;
		setLoading(true);
		await sendForgotPasswordCode(email.trim());
		setLoading(false);
		onSuccess(email.trim());
	};

	return (
		<Stack component="form" noValidate onSubmit={handleSubmit} spacing={2}>
			<Typography variant="body1" color="grey.500">
				{nls.ContentText.t()}
			</Typography>

			<TextField
				fullWidth
				required
				name="email"
				type="email"
				label={nls.EmailLabel.t()}
				value={email}
				onChange={handleEmailChange}
				onBlur={handleEmailBlur}
				inputProps={{ maxLength: 100, placeholder: nls.EmailPlaceholder.t() }}
				error={!!emailError}
				helperText={emailError}
				autoFocus
				size="small"
			/>

			<TextField
				fullWidth
				required
				name="emailVerify"
				type="email"
				label={nls.EmailVerifyLabel.t()}
				value={emailVerify}
				onChange={handleEmailVerifyChange}
				inputProps={{
					maxLength: 100,
					placeholder: nls.EmailPlaceholder.t(),
				}}
				error={!!emailVerifyError}
				helperText={emailVerifyError}
				size="small"
			/>

			<Box>
				<Button
					type="submit"
					variant="contained"
					disabled={!isFormReady || loading}
					fullWidth
					sx={buttonSX}
				>
					{nls.SendVerificationCodeButton.t()}
				</Button>
			</Box>

			<Stack direction="row" spacing={0.5} alignItems="center">
				<Typography variant="body1" component="span" sx={LoginSx.text}>
					{nls.AccountInfoRemember.t()}
				</Typography>
				{onGoToLogin ? (
					<Linkable type="inline" onClick={onGoToLogin} sx={LoginSx.inlineLink}>
						{nls.SignIn.t()}
					</Linkable>
				) : (
					<Linkable href={`/${RouteLocal.Login.route.t()}`} sx={LoginSx.inlineLink}>
						{nls.SignIn.t()}
					</Linkable>
				)}
			</Stack>
		</Stack>
	);
};
